import { formatRecoverySavedAt, readRecoveryCodesStatus } from '@src/utils/recoveryCodes';
import { AccountType, type UserAccountInfo } from '@lub0v/santa-utils';
import { isDotCom } from '@src/site';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, HintError, Note, Stack, Typography } from '@src/components';
import { AccessCodes } from '@src/pages/BoxPage/AccessCodes';
import { santaClient } from '@src/state/client';

export function AccountRecoveryCodes({ account }: { account: UserAccountInfo }) {
  const { t, i18n } = useTranslation();
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generating = useRef(false);
  const statusVersion = useRef(0);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const version = statusVersion.current;
    void readRecoveryCodesStatus().then(data => {
      if (active && version === statusVersion.current && !generating.current) setSavedAt(data.recoveryCodesSavedAt);
    }).catch(() => { /* Do not display an unverified acknowledgement. */ });
    return () => { active = false; };
  }, [account.id]);

  async function generate() {
    if (generating.current) return;
    generating.current = true;
    statusVersion.current += 1;
    setBusy(true);
    setError(null);
    // Once rotation starts, do not leave potentially invalidated old codes visible.
    setCodes([]);
    setSavedAt(null);
    try {
      const result = await santaClient.rotateRecoveryCodes({});
      if (result.ok && result.data?.recoveryCodes?.length) {
        setCodes(result.data.recoveryCodes);
      } else {
        setError('account.guestCodesError');
      }
    } catch {
      setError('account.guestCodesError');
    } finally {
      generating.current = false;
      setBusy(false);
    }
  }

  const providers = (account.accounts || []).filter(item => item.accountType !== AccountType.EMAIL);
  const providerNames: Partial<Record<AccountType, string>> = {
    [AccountType.VK]: 'VK', [AccountType.GOOGLE]: 'Google',
    [AccountType.TG]: 'Telegram', [AccountType.FB]: 'Facebook',
  };
  const identity = account.email || providers.map(item =>
    `${providerNames[item.accountType] || item.accountType}: ${item.username || item.accountId}`,
  ).join(', ') || account.username || `ID ${account.id}`;
  const copyIntroduction = t('account.recoveryCopyMessage', {
    site: isDotCom() ? 'santa-secret.com' : 'santa-secret.ru',
    identity,
  });

  return (
    <Stack gap={4} wide>
      {savedAt && <Typography as="p" role="body/base" testId="recovery-saved-at">
        {t('account.recoverySavedAt', { date: formatRecoverySavedAt(savedAt, i18n.language) })}
      </Typography>}
      <Note tone="warning">{t('account.recoveryWarning')}</Note>
      <Button variant="secondary" loading={busy} onClick={() => void generate()} testId="account-recovery-generate">
        {t('account.recoveryGenerate')}
      </Button>
      <HintError error={error} />
      {codes.length > 0 && (
        <AccessCodes key={codes[0]} kind="account" variant="flat" subject={t('account.recoveryTitle')}
          url={window.location.href} codes={codes} copyIntroduction={copyIntroduction} onContinue={date => { setCodes([]); setSavedAt(date || null); }} />
      )}
    </Stack>
  );
}
